import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { BehaviorSubject, Observable, of } from 'rxjs';
import { catchError, tap } from 'rxjs/operators';
import { environment } from '../../environment/environment';
import { AuthService } from './accounts/auth.service';

/**
 * App-wide currency state.
 *
 * KES is the settlement currency; this service only drives what a user *sees*.
 * The selectable list and the base->currency rates come from the corporate API
 * (`GET /currencies/`), which in turn reads Itinerary's `currency_rates_v2` —
 * one source of truth. The user's chosen currency is read from their profile
 * (`preferred_currency`) and can be updated live from the profile page.
 */
@Injectable({ providedIn: 'root' })
export class CurrencyService {
  private apiUrl = environment.corperate_base_url;

  /** Settlement/base currency. */
  base = 'KES';
  /** Currencies the user may pick (base + everything reachable from it). */
  currencies: string[] = ['KES'];
  /** base -> code conversion rates. */
  private ratesMap: { [code: string]: number } = {};

  private currencySubject = new BehaviorSubject<string>('KES');
  /** Emits the user's current display currency. */
  currency$ = this.currencySubject.asObservable();

  private ratesLoaded = false;
  private initialised = false;

  constructor(private http: HttpClient, private auth: AuthService) {}

  get displayCurrency(): string {
    return this.currencySubject.value;
  }

  setCurrency(code: string): void {
    this.currencySubject.next((code || 'KES').toUpperCase());
  }

  /**
   * Load the currency list + rates and the user's saved preference. Safe to
   * call from every component that shows money; only the first call does work.
   */
  init(): void {
    if (this.initialised) {
      return;
    }
    this.initialised = true;
    this.loadCurrencies().subscribe(() => this.loadUserPreference());
  }

  loadCurrencies(): Observable<any> {
    return this.http.get<any>(`${this.apiUrl}/currencies/`).pipe(
      tap((res) => {
        if (res) {
          this.base = (res.base || 'KES').toUpperCase();
          this.currencies = res.currencies || [this.base];
          this.ratesMap = res.rates || {};
          this.ratesLoaded = true;
        }
      }),
      catchError((err) => {
        console.error('Error loading currencies:', err);
        // Degrade to base-only rather than breaking the page.
        this.currencies = [this.base];
        return of(null);
      })
    );
  }

  private loadUserPreference(): void {
    let userId: string | undefined;
    try {
      userId = this.auth.user()?.user_id;
    } catch {
      userId = undefined;
    }
    if (!userId) {
      return;
    }
    this.http.get<any>(`${this.apiUrl}/users/${userId}`).subscribe({
      next: (res) => this.setCurrency(res?.preferred_currency || 'KES'),
      error: (err) => console.error('Error loading currency preference:', err),
    });
  }

  /**
   * Convert a base (KES) amount into `code`. Returns null when the amount is
   * unusable or no rate is available, so callers can fall back to the base.
   */
  convert(amountKes: number | null | undefined, code: string = this.displayCurrency): number | null {
    if (amountKes === null || amountKes === undefined || isNaN(Number(amountKes))) {
      return null;
    }
    const target = (code || this.base).toUpperCase();
    if (target === this.base) {
      return Number(amountKes);
    }
    const rate = this.ratesMap[target];
    return rate != null ? Number(amountKes) * rate : null;
  }

  /**
   * A display pair for a base amount: the converted value tagged with the
   * currency actually used (falls back to base + raw amount if unconvertible).
   */
  display(amountKes: number | null | undefined): { currency: string; amount: number | null } {
    const code = this.displayCurrency;
    const converted = this.convert(amountKes, code);
    if (converted === null) {
      const raw = amountKes === null || amountKes === undefined ? null : Number(amountKes);
      return { currency: this.base, amount: raw };
    }
    return { currency: code, amount: converted };
  }
}
